import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import { plannedActionStatus } from "./PrestashopAlignmentPage.helpers";
import {
  AlignmentInspector,
  ConfirmationDialog,
  actionStatus,
  candidateChanged,
  candidateString,
  isDerivedText,
  logicalReviewActions,
  requestErrorMessage,
} from "./PrestashopAlignmentPage";

function PrestashopAlignmentItemReview({
  codVS,
  token,
  utente,
  direction = "BOT_TO_VIG",
  onBeforeApply,
  onApplied,
}) {
  const normalizedDirection = direction === "VIG_TO_BOT" ? "VIG_TO_BOT" : "BOT_TO_VIG";
  const directionLabel = normalizedDirection === "VIG_TO_BOT" ? "VIG → BOT" : "BOT → VIG";
  const embeddedLabel = normalizedDirection === "VIG_TO_BOT" ? "Export a Bottero" : "Import Bottero";
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [drafts, setDrafts] = useState({});
  const [reviewedIds, setReviewedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const analyzedKeyRef = useRef(null);
  const selectedItemIdRef = useRef(null);
  const articleSessionRef = useRef(0);
  const detailAbortRef = useRef(null);
  const authHeaders = useMemo(
    () => ({ Authorization: "Bearer " + token }),
    [token],
  );
  const canWrite = Boolean(
    utente?.permessiAPI?.["update:prestashop"] &&
    utente?.permessiAPI?.["update:database"],
  );
  const auditUsername = String(utente?.username || "").trim();

  const loadDetail = useCallback(async (itemId, options = {}) => {
    if (!token || !itemId) return null;
    const session = articleSessionRef.current;
    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;
    const isCurrent = () => session === articleSessionRef.current && !controller.signal.aborted;
    if (!options.silent) setLoading(true);
    try {
      const response = await axios.get(
        apiUrl + "/moda/prestashop-alignment/items/" + itemId,
        { headers: authHeaders, signal: controller.signal },
      );
      if (!isCurrent()) return null;
      const detail = response?.data?.data || null;
      setSelectedItem(detail);
      return detail;
    } catch (requestError) {
      if (!isCurrent()) return null;
      setError(requestErrorMessage(
        requestError,
        "Impossibile caricare il dettaglio dell'articolo.",
      ));
      return null;
    } finally {
      if (isCurrent() && !options.silent) setLoading(false);
    }
  }, [authHeaders, token]);

  const analyzeArticle = useCallback(async (force = false) => {
    const normalizedCodVS = String(codVS || "").trim();
    const analysisKey = normalizedCodVS + ":" + normalizedDirection;
    if (!token || !normalizedCodVS || (!force && analyzedKeyRef.current === analysisKey)) return;
    if (!canWrite) {
      setError("Permessi update:prestashop e update:database richiesti per avviare l'analisi.");
      return;
    }
    if (!auditUsername) {
      setError("Username Domino non disponibile: impossibile registrare l'analisi.");
      return;
    }
    analyzedKeyRef.current = analysisKey;
    const session = articleSessionRef.current;
    setLoading(true);
    setError("");
    setNotice("Analisi " + directionLabel + " in corso…");
    try {
      const response = await axios.post(
        apiUrl + "/moda/prestashop-alignment/analyze-one",
        {
          codVS: normalizedCodVS,
          direction: normalizedDirection,
          requestedBy: auditUsername,
        },
        { headers: authHeaders },
      );
      if (session !== articleSessionRef.current) return;
      const itemId = response?.data?.data?.itemId;
      if (!itemId) throw new Error("L'analisi non ha restituito un itemId.");
      const detail = await loadDetail(itemId, { silent: true });
      if (session !== articleSessionRef.current) return;
      setNotice(detail ? "Analisi " + directionLabel + " completata. Puoi revisionare le proposte." : "");
    } catch (requestError) {
      if (session !== articleSessionRef.current) return;
      analyzedKeyRef.current = null;
      setSelectedItem(null);
      setError(requestErrorMessage(requestError, "Impossibile analizzare l'articolo."));
      setNotice("");
    } finally {
      if (session === articleSessionRef.current) setLoading(false);
    }
  }, [auditUsername, authHeaders, canWrite, codVS, directionLabel, loadDetail, normalizedDirection, token]);

  useEffect(() => {
    analyzedKeyRef.current = null;
    selectedItemIdRef.current = null;
    setSelectedItem(null);
    setSelectedIds(new Set());
    setDrafts({});
    setReviewedIds(new Set());
    setError("");
    setNotice("");
    setConfirmation(null);
    setLoading(false);
    return () => {
      articleSessionRef.current += 1;
      detailAbortRef.current?.abort();
    };
  }, [codVS, normalizedDirection]);

  useEffect(() => {
    if (normalizedDirection === "BOT_TO_VIG") void analyzeArticle();
  }, [analyzeArticle, normalizedDirection]);

  const selectedItemActions = selectedItem?.actions;
  useEffect(() => {
    const actions = Array.isArray(selectedItemActions) ? selectedItemActions : [];
    const currentItemId = selectedItem?.id ?? null;
    const sameItem = currentItemId !== null &&
      String(selectedItemIdRef.current) === String(currentItemId);
    setDrafts((current) => {
      const next = {};
      actions.filter(isDerivedText).forEach((action) => {
        const key = String(action.id);
        next[key] = sameItem && Object.prototype.hasOwnProperty.call(current, key)
          ? current[key]
          : candidateString(action);
      });
      return next;
    });
    setSelectedIds((current) => {
      if (!sameItem) return new Set();
      const proposedIds = new Set(
        logicalReviewActions(actions)
          .filter((action) => actionStatus(action) === "PROPOSED")
          .map((action) => Number(action.id)),
      );
      return new Set(Array.from(current).filter((id) => proposedIds.has(Number(id))));
    });
    setReviewedIds((current) => {
      if (!sameItem) return new Set();
      const proposedIds = new Set(actions.filter((action) => actionStatus(action) === "PROPOSED").map((action) => Number(action.id)));
      return new Set(Array.from(current).filter((id) => proposedIds.has(id)));
    });
    selectedItemIdRef.current = currentItemId;
  }, [selectedItem?.id, selectedItemActions]);

  const toggleAction = (action, checked) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(Number(action.id));
      else next.delete(Number(action.id));
      return next;
    });
  };

  const selectAllActions = (actions) => {
    setSelectedIds(new Set(actions.map((action) => Number(action.id)).filter(Number.isFinite)));
  };

  const changeDraft = (action, value) => {
    setDrafts((current) => ({ ...current, [String(action.id)]: value }));
    if (plannedActionStatus(action) === "NEEDS_EDITORIAL_REVIEW" && !candidateChanged(action, value)) {
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(Number(action.id));
        return next;
      });
    }
  };
  const confirmIntro = (action, value) => {
    setDrafts((current) => ({ ...current, [String(action.id)]: value }));
    setReviewedIds((current) => new Set(current).add(Number(action.id)));
    setSelectedIds((current) => new Set(current).add(Number(action.id)));
  };

  const reconcile = useCallback(async (silent = false) => {
    if (!selectedItem?.id || !canWrite || busy) return;
    if (!silent) {
      setBusy(true);
      setError("");
      setNotice("");
    }
    try {
      await axios.post(
        apiUrl + "/moda/prestashop-alignment/items/" + selectedItem.id + "/reconcile",
        { requestedBy: auditUsername },
        { headers: authHeaders },
      );
      await loadDetail(selectedItem.id, { silent: true });
      if (!silent) setNotice("Stato downstream aggiornato.");
    } catch (requestError) {
      if (!silent) setError(requestErrorMessage(requestError, "Impossibile aggiornare lo stato downstream."));
    } finally {
      if (!silent) setBusy(false);
    }
  }, [auditUsername, authHeaders, busy, canWrite, loadDetail, selectedItem?.id]);

  useEffect(() => {
    if (!canWrite || !selectedItem?.id || !["APPLYING", "WAITING_DOWNSTREAM"].includes(selectedItem.status)) return undefined;
    const interval = window.setInterval(() => void reconcile(true), 12000);
    return () => window.clearInterval(interval);
  }, [canWrite, reconcile, selectedItem?.id, selectedItem?.status]);

  const selectedActions = logicalReviewActions(selectedItem?.actions)
    .filter((action) => selectedIds.has(Number(action.id)));

  const runConfirmedOperation = async () => {
    if (!confirmation || !selectedItem?.id) return;
    if (confirmation === "APPLY" && onBeforeApply) {
      const allowed = await onBeforeApply();
      if (allowed === false) {
        setConfirmation(null);
        setError("Salva prima le modifiche locali al prodotto, quindi ripeti l'applicazione.");
        return;
      }
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      let operationResult = null;
      if (confirmation === "APPROVE") {
        const candidateEdits = selectedActions
          .filter((action) => isDerivedText(action) && (reviewedIds.has(Number(action.id)) || candidateChanged(action, drafts[String(action.id)])))
          .map((action) => ({ actionId: Number(action.id), candidateValue: drafts[String(action.id)] }));
        await axios.post(
          apiUrl + "/moda/prestashop-alignment/items/" + selectedItem.id + "/approve",
          {
            actionIds: selectedActions.map((action) => Number(action.id)),
            candidateEdits,
            approvedBy: auditUsername,
          },
          { headers: authHeaders },
        );
        setNotice("Azioni approvate. Verifica il riepilogo e applicale quando sei pronto.");
      } else if (confirmation === "APPLY") {
        const response = await axios.post(
          apiUrl + "/moda/prestashop-alignment/items/" + selectedItem.id + "/apply",
          { appliedBy: auditUsername },
          { headers: authHeaders },
        );
        operationResult = response?.data?.data || null;
        const refreshed = onApplied ? await onApplied(operationResult) : true;
        setNotice(refreshed === false
          ? "Applicazione avviata, ma il prodotto aperto non è stato ricaricato da Domino. Chiudilo e riaprilo prima di modificarlo."
          : normalizedDirection === "VIG_TO_BOT"
            ? "Applicazione avviata verso Bottero. Il prodotto aperto è stato riallineato a Domino."
            : "Applicazione avviata. Il prodotto aperto è stato riallineato a Domino.");
      } else if (confirmation === "CANCEL") {
        await axios.post(
          apiUrl + "/moda/prestashop-alignment/items/" + selectedItem.id + "/cancel",
          { cancelledBy: auditUsername },
          { headers: authHeaders },
        );
        setNotice("Lavorazione annullata.");
      } else if (confirmation === "REANALYZE") {
        await axios.post(
          apiUrl + "/moda/prestashop-alignment/items/" + selectedItem.id + "/reanalyze",
          { requestedBy: auditUsername },
          { headers: authHeaders },
        );
        setSelectedIds(new Set());
        setDrafts({});
        setReviewedIds(new Set());
        setNotice("Articolo rianalizzato con i dati correnti.");
      }
      setConfirmation(null);
      await loadDetail(selectedItem.id, { silent: true });
    } catch (requestError) {
      setConfirmation(null);
      setError(requestErrorMessage(requestError, "Operazione non completata."));
      await loadDetail(selectedItem.id, { silent: true });
    } finally {
      setBusy(false);
    }
  };

  const retryAction = async (action) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await axios.post(
        apiUrl + "/moda/prestashop-alignment/actions/" + action.id + "/retry",
        { requestedBy: auditUsername },
        { headers: authHeaders },
      );
      setNotice("Retry avviato per l'azione selezionata.");
      await loadDetail(selectedItem.id, { silent: true });
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "Retry non completato."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="alignment-embedded-review" aria-label={embeddedLabel}>
      {normalizedDirection === "VIG_TO_BOT" && !selectedItem && !loading && !error ? (
        <div className="pa3">
          <p>Avvia il confronto con Bottero per revisionare le proposte di export. Potrai poi approvare e applicare le modifiche.</p>
          <button
            type="button"
            className="alignment-button alignment-button--primary"
            disabled={!token || !String(codVS || "").trim()}
            onClick={() => void analyzeArticle()}
          >
            Avvia l'export
          </button>
        </div>
      ) : null}
      {(error || notice) ? (
        <div className="alignment-embedded-messages">
          {error ? <div role="alert" className="pa3 sfondoColAltScuro scrittaColChiara">{error}</div> : null}
          {notice ? <div role="status" className="alignment-notice pa3">{notice}</div> : null}
          {error && !selectedItem ? (
            <button type="button" className="alignment-button alignment-button--secondary" disabled={loading} onClick={() => void analyzeArticle(true)}>
              Riprova analisi
            </button>
          ) : null}
        </div>
      ) : null}
      {selectedItem || loading ? <AlignmentInspector
        item={selectedItem}
        loading={loading}
        token={token}
        onBack={null}
        selectedIds={selectedIds}
        drafts={drafts}
        reviewedIds={reviewedIds}
        canWrite={canWrite}
        busy={busy}
        onToggle={toggleAction}
        onSelectAll={selectAllActions}
        onDraftChange={changeDraft}
        onConfirmIntro={confirmIntro}
        onConfirm={setConfirmation}
        onReconcile={(silent) => reconcile(Boolean(silent))}
        onRefresh={() => selectedItem?.id ? loadDetail(selectedItem.id, { silent: true }) : null}
        onRetry={retryAction}
        embedded
      /> : null}
      <ConfirmationDialog
        operation={confirmation}
        actions={selectedActions}
        onCancel={() => setConfirmation(null)}
        onConfirm={runConfirmedOperation}
        busy={busy}
      />
    </section>
  );
}

export default PrestashopAlignmentItemReview;
